/**
 * MobileTopBar — clean floating top bar for the mobile creator studio.
 *
 * Layout: [hamburger]    [✦ MOODCARD wordmark]    [pencil · create]
 *
 * iter-73: replaces the legacy "back · moodcard" header that mixed
 * navigation chrome with brand on mobile CardDetail. Per the user's
 * design spec, the resting screen must look like Instagram's editor —
 * nothing on the top bar except brand identity + the two universal
 * affordances. Everything else hides inside the hamburger sheet.
 *
 * iter-85: the right-side bell has been replaced with a pencil "create"
 * affordance. The user wanted a globally-available create entrypoint
 * (especially for signed-out viewers landing on a `/c/:slug` page who
 * need ONE tap to make their own card). The pencil routes to `/` (home
 * studio). Bell notifications surface only on specific pages that opt in
 * by passing `rightMode="bell"`.
 */
import React from "react";
import { Menu, Bell, PenLine } from "lucide-react";
import { useNavigate } from "react-router-dom";
import QuotaChip from "../QuotaChip";

// iter-74: brand mark — small neon-green hollow square with an inner dot,
// matches the "green box" in the user's mobile mockup. Standalone SVG so
// we don't depend on a lucide variant.
function GreenSquareMark({ className = "w-5 h-5" }) {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            className={className}
            aria-hidden="true"
        >
            <rect
                x="3.5"
                y="3.5"
                width="17"
                height="17"
                rx="3.5"
                stroke="#34d399"
                strokeWidth="2"
            />
            <rect
                x="9"
                y="9"
                width="6"
                height="6"
                rx="1"
                fill="#34d399"
            />
        </svg>
    );
}

export default function MobileTopBar({
    onOpenMenu,
    onOpenNotifications,
    onCreate,
    rightMode = "create", // "create" (default · pencil → /) or "bell" (notifications)
    hasNotifications = false,
    showLeft = true,
}) {
    const navigate = useNavigate();
    const handleRight = () => {
        if (rightMode === "bell") {
            onOpenNotifications?.();
        } else {
            if (onCreate) onCreate();
            else navigate("/");
        }
    };
    const RightIcon = rightMode === "bell" ? Bell : PenLine;
    const rightLabel = rightMode === "bell" ? "notifications" : "make a new card";
    const rightTestId = rightMode === "bell" ? "mobile-top-bell" : "mobile-top-create";

    return (
        <header
            className="lg:hidden sticky top-0 z-30 w-full pt-3 pb-2 px-4 flex items-center justify-between bg-app/85 backdrop-blur-md"
            data-testid="mobile-top-bar"
        >
            <button
                type="button"
                onClick={onOpenMenu}
                aria-label="open menu"
                className={`w-10 h-10 rounded-full bg-white/4 border border-white/8 backdrop-blur-md flex items-center justify-center text-white/85 hover:bg-white/8 transition-colors ${
                    showLeft ? "" : "invisible"
                }`}
                data-testid="mobile-top-menu"
            >
                <Menu className="w-4 h-4" strokeWidth={1.6} />
            </button>

            <div className="flex items-center gap-2 text-white/95">
                <GreenSquareMark className="w-5 h-5" />
                <span className="font-display text-base tracking-[0.18em] uppercase">
                    moodcard
                </span>
            </div>

            {/* iter-93 — quota chip shown when free user has 0-1 pours left.
                Wraps in absolute container so it doesn't collide with brand
                center alignment. */}
            <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 pointer-events-none">
                <div className="pointer-events-auto">
                    <QuotaChip />
                </div>
            </div>

            <button
                type="button"
                onClick={handleRight}
                aria-label={rightLabel}
                className="relative w-10 h-10 rounded-full bg-white/4 border border-white/8 backdrop-blur-md flex items-center justify-center text-white/85 hover:bg-white/8 transition-colors"
                data-testid={rightTestId}
            >
                <RightIcon className="w-4 h-4" strokeWidth={1.6} />
                {hasNotifications && rightMode === "bell" && (
                    <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-rose-400" />
                )}
            </button>
        </header>
    );
}
